import { ReactNode } from 'react';

import dayjs from 'dayjs';

import { DATE } from '@cast/constants';
import {
  Card,
  Icons,
  InfoTooltip,
  LabelChip,
  ProgressBar,
  Skeleton,
  ShadowDivider,
  Tooltip,
  Typography,
  Box,
  Stack,
  tokens,
  ChoicePanel,
} from '@cast/design-system';
import { toNumberWithSeparators } from '@cast/utils';

type OverviewCardBadge = {
  label: string;
  endDate?: string;
  tooltip?: ReactNode;
};

type OverviewCardUsage = {
  value: string | number;
  unit?: string;
  subline?: ReactNode;
  infoTooltip?: string;
};

type OverviewCardCredits = {
  used: number;
  total: number;
  remaining: number;
  period?: string;
  periodTooltip?: ReactNode;
};

type OverviewCardProps = {
  title: string;
  badge?: OverviewCardBadge;
  usage: OverviewCardUsage;
  credits?: OverviewCardCredits;
  onClick: () => void;
  /** Applied to the card root — shared by all cards so tests can select and
      order them via a testid instead of positional/xpath selectors. */
  testId?: string;
};

export const OverviewCardSkeleton = () => (
  <Card
    sx={{
      display: 'flex',
      alignItems: 'stretch',
      overflow: 'hidden',
      height: 100,
    }}
  >
    <Stack sx={{ width: 360, flexShrink: 0, p: 24, justifyContent: 'center' }}>
      <Skeleton variant="text" width={160} height={28} />
    </Stack>

    <Box
      sx={{
        flex: '1 0 0',
        display: 'flex',
        alignItems: 'center',
        p: 24,
        position: 'relative',
        minWidth: 0,
      }}
    >
      <ShadowDivider
        orientation="vertical"
        direction="right"
        absolute
        left
        top
        bottom
        size={16}
        opacity={0.06}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flex: '1 0 0',
          minWidth: 0,
        }}
      >
        <Stack gap={4} sx={{ width: 160, flexShrink: 0 }}>
          <Skeleton variant="text" width={40} height={12} />
          <Skeleton variant="text" width={80} height={36} />
        </Stack>

        <Stack sx={{ width: 370, flexShrink: 0 }} gap={8}>
          <Skeleton variant="text" width={60} height={12} />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={8}
            sx={{ borderRadius: '8px' }}
          />
          <Skeleton variant="text" width={120} height={18} />
        </Stack>

        <Skeleton variant="circular" width={32} height={32} />
      </Box>
    </Box>
  </Card>
);

export const OverviewCard = ({
  title,
  badge,
  usage,
  credits,
  onClick,
  testId,
}: OverviewCardProps) => {
  const progressValue =
    credits && credits.total > 0 ? (credits.used / credits.total) * 100 : 0;

  return (
    <ChoicePanel
      onClick={onClick}
      testId={testId}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        overflow: 'hidden',
      }}
    >
      {/* Left panel: title + optional badge */}
      <Stack
        sx={{
          width: 360,
          flexShrink: 0,
          p: 24,
          justifyContent: 'space-between',
        }}
      >
        <Typography color={tokens.theme.fg.structural.primary} variant="h5">
          {title}
        </Typography>

        {badge && (
          <Stack direction="row" alignItems="center" gap={8}>
            <LabelChip
              size="sm"
              sx={{
                bgcolor: tokens.theme.bg.status.successStrong,
                color: tokens.theme.fg.status.onSuccessStrong,
              }}
              endIcon={
                badge.tooltip ? (
                  <Tooltip title={badge.tooltip}>
                    <Box component="span" sx={{ display: 'flex' }}>
                      <Icons.Info size={10} />
                    </Box>
                  </Tooltip>
                ) : undefined
              }
            >
              {badge.label}
            </LabelChip>

            {badge.endDate && (
              <Typography
                variant="P12R"
                color={tokens.theme.fg.structural.secondary}
              >
                Until {dayjs(badge.endDate).format(DATE)}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>

      {/* Right panel: usage + credits + arrow */}
      <Box
        sx={{
          flex: '1 0 0',
          display: 'flex',
          alignItems: 'center',
          p: 24,
          position: 'relative',
          minWidth: 0,
        }}
      >
        <ShadowDivider
          orientation="vertical"
          direction="right"
          absolute
          left
          top
          bottom
          size={16}
          opacity={0.06}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flex: '1 0 0',
            minWidth: 0,
          }}
        >
          {/* Usage metric */}
          <Stack gap={4} sx={{ width: 200, flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" gap={4}>
              <Typography
                variant="L10R"
                color={tokens.theme.fg.structural.secondary}
              >
                Usage this month
              </Typography>
              <InfoTooltip
                title={
                  usage.infoTooltip ??
                  'Calculated as raw CPUs normalized over the total hours of the current billing month. Usage builds progressively throughout the month.'
                }
                iconSize={12}
              />
            </Stack>
            <Stack direction="row" alignItems="baseline" gap={8}>
              <Typography
                color={tokens.theme.fg.structural.primary}
                variant="h3"
              >
                {typeof usage.value === 'number'
                  ? usage.value.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : usage.value}
              </Typography>
              {usage.unit && (
                <Typography
                  variant="L10R"
                  color={tokens.theme.fg.structural.secondary}
                >
                  {usage.unit}
                </Typography>
              )}
            </Stack>
            {usage.subline && (
              <Typography
                variant="P12R"
                color={tokens.theme.fg.structural.secondary}
              >
                {usage.subline}
              </Typography>
            )}
          </Stack>

          {/* Credits progress */}
          {credits && credits.total > 0 && (
            <Stack
              sx={{
                height: 50,
                width: 370,
                flexShrink: 0,
                justifyContent: 'space-between',
              }}
            >
              <Stack
                direction="row"
                alignItems="baseline"
                justifyContent="space-between"
              >
                <Typography
                  variant="L10R"
                  color={tokens.theme.fg.structural.secondary}
                >
                  Credits
                </Typography>
                {credits.period &&
                  (credits.periodTooltip ? (
                    <Tooltip title={credits.periodTooltip}>
                      <Stack direction="row" alignItems="center" gap={4}>
                        <Typography
                          variant="L10R"
                          color={tokens.theme.fg.structural.secondary}
                        >
                          {credits.period}
                        </Typography>
                        <Icons.Clock
                          size={16}
                          color={tokens.theme.fg.structural.secondary}
                        />
                      </Stack>
                    </Tooltip>
                  ) : (
                    <Stack direction="row" alignItems="center" gap={4}>
                      <Typography
                        variant="L10R"
                        color={tokens.theme.fg.structural.secondary}
                      >
                        {credits.period}
                      </Typography>
                      <Icons.Clock
                        size={16}
                        color={tokens.theme.fg.structural.secondary}
                      />
                    </Stack>
                  ))}
              </Stack>

              <Stack gap={4}>
                <ProgressBar
                  size="lg"
                  variant="determinate"
                  value={progressValue}
                />
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" gap={4}>
                    <Typography
                      variant="P12B"
                      color={tokens.theme.fg.structural.secondary}
                    >
                      {toNumberWithSeparators(Math.round(credits.used))} /{' '}
                      {toNumberWithSeparators(Math.round(credits.total))}
                    </Typography>
                    <Typography
                      variant="P12R"
                      color={tokens.theme.fg.structural.secondary}
                    >
                      used
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" gap={4}>
                    <Typography
                      variant="P12B"
                      color={tokens.theme.fg.structural.secondary}
                    >
                      {toNumberWithSeparators(
                        Math.round(credits.remaining ?? 0)
                      )}
                    </Typography>
                    <Typography
                      variant="P12R"
                      color={tokens.theme.fg.structural.secondary}
                    >
                      remaining
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          )}

          {/* Navigate arrow */}
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Icons.ArrowRight size={32} />
          </Box>
        </Box>
      </Box>
    </ChoicePanel>
  );
};
